import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Shield, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { submitLeadWithGeoLocation } from '../services/centralLeadApi';
import { toast } from 'sonner';
import { countryCodes } from '../utils/countryCodes';

const HeroSection = ({ onOpenModal }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', description: '' });
  const [countryCode, setCountryCode] = useState('+1');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        countryCode: countryCode,
        country: countryCodes.find(c => c.code === countryCode)?.country || 'US',
        description: formData.description || 'Hero Form Submission'
      };

      const result = await submitLeadWithGeoLocation(leadData, null, 'Home Hero Section');
      
      if (result.success) {
        toast.success(result.message || 'Request submitted! Our team will contact you shortly.');
        setFormData({ name: '', email: '', phone: '', description: '' });
      } else {
        toast.error(result.message || 'Submission failed. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Hero form submission error:', error);
      toast.error('Submission failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative flex items-start bg-gradient-to-br from-[#012169] via-[#013d9e] to-[#012169] overflow-hidden">
      {/* Modern Abstract Background */}
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1620641788421-7a1c342ea42e" 
          alt="Abstract gradient background"
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Enhanced Animated Background Overlay */}
      <div className="absolute inset-0 opacity-15">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#F97316] rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#8B5CF6] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-[#10B981] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Content - Desktop: Order 1, Mobile: Order 2 */}
          <div className="text-left order-2 lg:order-1">
            {/* Main Heading - Hidden on Mobile */}
            <h1 className="hidden lg:block text-3xl sm:text-4xl md:text-5xl font-black mb-8 leading-[1.1] tracking-tight">
              <span className="text-white">Trademark Registration</span>
              <br />
              <span className="text-[#F97316]">
                From $45 with Attorney
              </span>
            </h1>

            {/* Subtitle */}
            <p className="hidden lg:block text-base md:text-base text-white/90 mb-10 leading-relaxed max-w-xl font-light">
              Protect your brand name, logo, and slogan with USPTO trademark registration. Licensed IP attorneys handle your filing from search to approval. Join 10,000+ businesses who trust us with their intellectual property.
            </p>

            {/* Key Benefits - Hidden on Mobile */}
            <div className="hidden lg:flex flex-wrap gap-8 mb-10">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-[#F97316]/30 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 border border-[#F97316]/40 hover:scale-110 transition-transform duration-300">
                  <CheckCircle className="h-6 w-6 text-[#F97316]" />
                </div>
                <div>
                  <div className="text-base font-bold text-white mb-1">98% Approval Rate</div>
                  <div className="text-sm text-white/80">Industry-leading success</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-[#8B5CF6]/30 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 border border-[#8B5CF6]/40 hover:scale-110 transition-transform duration-300">
                  <Sparkles className="h-6 w-6 text-[#8B5CF6]" />
                </div>
                <div>
                  <div className="text-base font-bold text-white mb-1">USPTO Registered</div>
                  <div className="text-sm text-white/80">Licensed IP attorneys</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form Card - Desktop: Order 2, Mobile: Order 1 */}
          <div className="relative order-1 lg:order-2">
            <div className="bg-white rounded-2xl shadow-2xl p-8 relative overflow-hidden group hover:shadow-[0_20px_60px_rgba(249,115,22,0.4)] transition-all duration-300">
              {/* Decorative corner */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#F97316] to-[#8B5CF6] opacity-10 rounded-bl-full"></div>
              
              <div className="relative z-10">
                {/* Mobile Hero Heading - Shows only on mobile/tablet */}
                <div className="lg:hidden text-center mb-6">
                  <h1 className="text-2xl sm:text-3xl font-black mb-4 leading-tight">
                    <span className="text-[#012169]">Trademark Registration</span>
                    <br />
                    <span className="text-[#F97316]">From $45 with Attorney</span>
                  </h1>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                    Protect your brand name, logo, and slogan with USPTO trademark registration.
                  </p>
                </div>

                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-3xl font-black text-[#012169] mb-1">Get Started Today</h2>
                    <p className="text-sm text-[#F97316] font-semibold">Free Legal Consultation</p>
                  </div>
                  <div className="w-14 h-14 bg-gradient-to-br from-[#012169] to-[#8B5CF6] rounded-2xl flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300">
                    <Shield className="h-7 w-7 text-white" />
                  </div>
                </div>
                
                <p className="text-gray-700 mb-6 leading-relaxed">Start your trademark registration today. Our IP attorneys provide a free consultation, comprehensive trademark search, and expert USPTO filing. Protect your brand before competitors do.</p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="group">
                    <Input
                      type="text"
                      placeholder="Full Name *"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border-2 border-gray-200 focus:border-[#012169] rounded-lg px-4 py-3 text-gray-800 transition-all duration-300 group-hover:border-[#F97316]"
                      required
                    />
                  </div>
                  
                  <div className="group">
                    <Input
                      type="email"
                      placeholder="Email Address *"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full border-2 border-gray-200 focus:border-[#012169] rounded-lg px-4 py-3 text-gray-800 transition-all duration-300 group-hover:border-[#F97316]"
                      required
                    />
                  </div>
                  
                  <div className="group">
                    <div className="flex items-stretch gap-2">
                      <select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="w-28 px-3 py-3 border-2 border-gray-200 rounded-lg bg-white text-sm font-medium text-gray-700 focus:outline-none focus:border-[#012169] cursor-pointer transition-all duration-300 h-auto"
                      >
                        {countryCodes.map((country, index) => (
                          <option key={index} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                      <Input
                        type="tel"
                        placeholder="Phone Number *"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^\d\s\-]/g, '');
                          setFormData({ ...formData, phone: value });
                        }}
                        className="flex-1 border-2 border-gray-200 focus:border-[#012169] rounded-lg px-4 py-3 text-gray-800 transition-all duration-300 h-auto"
                        required
                      />
                    </div>
                  </div>

                  <div className="group">
                    <Textarea
                      placeholder="Tell us about your needs... *"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full border-2 border-gray-200 focus:border-[#012169] rounded-lg px-4 py-3 text-gray-800 transition-all duration-300 resize-none"
                      rows={3}
                      required
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#F97316] to-[#F97316] hover:from-[#EA580C] hover:to-[#F97316] text-white font-bold text-base py-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] group"
                  >
                    {isSubmitting ? 'Submitting...' : (
                      <span className="flex items-center justify-center gap-2">
                        Get Free Consultation
                        <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    )}
                  </Button>
                </form>
                
                <p className="text-xs text-gray-500 text-center mt-4">
                  ✓ No Obligation • ✓ Secure & Confidential • ✓ Free Expert Advice
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
